import { Immutable } from "immer";
import React from "react";

import {
    IAddon,
    IBundleGroup,
    InternalProduct,
} from "../../../models/catalogue";
import { IImageURL, IProductID } from "../../../models/nominals";
import { useAppSelector } from "../../../store/reducers";
import { ConfiguratorUpsell } from "../ConfiguratorUpsell";
import { ConfiguratorUpsellRadio } from "../ConfiguratorUpsellRadio";
import { OptionPriceType } from "../ConfiguratorUpsells";
import { defaults } from "../defaults";
import {
    baseVariantSelector,
    getUpgradeID,
    upgradeBundlesSelector,
} from "../selectors";
import {
    buildUpsellPrice,
    buildUpsellProductName,
} from "../utils/upsellPricing";

const buildProduct = (
    suggested_products: readonly InternalProduct[],
    selectedUpgrades: readonly IAddon[],
): InternalProduct => {
    let currentProduct = suggested_products[0];
    suggested_products.forEach((product) => {
        const upgradeID = getUpgradeID(product);
        const selectedUpgrade = selectedUpgrades.find(
            (addon) => addon.productID === upgradeID,
        );
        if (selectedUpgrade) {
            currentProduct = product;
        }
    });
    return currentProduct;
};

const isSelected = (
    upgradeID: IProductID,
    selectedUpgrades: readonly IAddon[],
): boolean => selectedUpgrades.some((addon) => addon.productID === upgradeID);

const buildImage = (
    product: InternalProduct,
    bundle_group: IBundleGroup,
): IImageURL | null => {
    const bundleImage = bundle_group.image;
    if (bundleImage) {
        return bundleImage;
    }
    const productImage = product.parent
        ? product.parent.images[0]
        : product.images[0];
    if (productImage) {
        return productImage.original;
    }
    return null;
};

export const ConfiguratorUpsellContainer = (
    props: Immutable<{
        optionPriceType: OptionPriceType;
        bundleType: string;
        selectedUpgrades: readonly IAddon[];
        onSelectionChange: (
            updates: Array<{ productID: IProductID; quantity: number }>,
        ) => void;
        // Caller decides which presentation to use (radio vs card). Keeping
        // the site-detection out of this container so it stays a pure
        // data-mapping component — see ConfiguratorUpsells for the gating.
        useRadioStyle?: boolean;
    }>,
): React.ReactNode => {
    const baseVariant = useAppSelector((rootState) => {
        const state = rootState.configurator || defaults;
        return baseVariantSelector(state);
    });

    const upgradeBundles = useAppSelector((rootState) => {
        const state = rootState.configurator || defaults;
        const variant = baseVariantSelector(state);
        return upgradeBundlesSelector(
            state.entities.concreteBundles,
            variant,
            props.bundleType,
        );
    });

    if (!baseVariant) {
        return null;
    }
    if (upgradeBundles.length <= 0) {
        return null;
    }

    const upgradeOptionElems = upgradeBundles.map((bundle) => {
        const cardProduct = buildProduct(
            bundle.suggested_products,
            props.selectedUpgrades,
        );
        const upgradeID = getUpgradeID(cardProduct);
        const { price, strikethroughPrice } = buildUpsellPrice(
            cardProduct,
            baseVariant,
            {
                isFreeGift: props.optionPriceType === OptionPriceType.FREE_GIFT,
                isRelative: props.optionPriceType === OptionPriceType.RELATIVE,
                isOneProduct: true,
            },
        );
        if (props.useRadioStyle) {
            return (
                <ConfiguratorUpsellRadio
                    key={bundle.id}
                    bundleID={bundle.bundle_group.id}
                    sectionTitle={bundle.bundle_group.description}
                    lineOne={bundle.bundle_group.headline}
                    lineThree={price}
                    productUpgradeID={upgradeID}
                    isSelected={isSelected(upgradeID, props.selectedUpgrades)}
                    isUnavailable={
                        !cardProduct.availability.is_available_to_buy
                    }
                />
            );
        }
        return (
            <ConfiguratorUpsell
                key={bundle.id}
                bundleType={props.bundleType}
                bundleID={bundle.bundle_group.id}
                sectionTitle={bundle.bundle_group.description}
                lineOne={bundle.bundle_group.headline}
                lineTwo={buildUpsellProductName(cardProduct)}
                lineThree={price}
                lineThreeStrikethrough={strikethroughPrice}
                productUpgradeID={upgradeID}
                isSelected={isSelected(upgradeID, props.selectedUpgrades)}
                cardImage={buildImage(cardProduct, bundle.bundle_group)}
                isUnavailable={!cardProduct.availability.is_available_to_buy}
                unavailableMessage={cardProduct.availability.short_message}
            />
        );
    });
    return <>{upgradeOptionElems}</>;
};
